import { useState, useMemo, useEffect } from 'react';
import ProjectForm from './ProjectForm';
import ProjectsPage, { mockProjects, CURRENCY_META, curOf, fmtMoney, PaymentStatusBadges } from './ProjectsPage';
import FreelancersPage, { MOCK_FREELANCERS, docExpiryStatus, formatDate, flaggedDocuments } from './FreelancersPage';
import { TasksProvider, useTasks } from './TasksContext';
import TasksPage from './TasksPage';
import CompaniesPage, { initCompanies } from './CompaniesPage';
import EquipmentPage from './EquipmentPage';
import ProfilePage from './ProfilePage';
import MyProjectsPage from './MyProjectsPage';
import MyCalendarPage from './MyCalendarPage';
import TeamCalendarPage from './TeamCalendarPage';
import OnboardingPage from './OnboardingPage';
import FreelancerCardPage from './FreelancerCardPage';
import GuidePage from './GuidePage';
import ConsentSignPage from './ConsentSignPage';
import MyDocumentsPage from './MyDocumentsPage';
import ActivityLogPage, { actionLabel, formatTime } from './ActivityLogPage';
import api from './api';

// ─── Constants ───────────────────────────────────────────────────────────────

const PROJECT_TYPES = ['Crewing', 'Dry hire', 'Permanent installation'];

const STATUSES = ['Planning', 'Confirmed', 'Active', 'On Hold', 'Completed', 'Canceled'];

const statusConfig = {
  'Planning':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  'Confirmed':  { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Active':     { color: 'var(--success-text)', bg: 'var(--success-bg)' },
  'On Hold':    { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Completed':  { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)' },
  'Canceled':   { color: 'var(--accent)', bg: 'var(--danger-bg)' },
};

const ALL_COLUMNS = [
  { key: 'name',        label: 'Project Name' },
  { key: 'company',     label: 'Company' },
  { key: 'type',        label: 'Type' },
  { key: 'location',    label: 'Location' },
  { key: 'startDate',   label: 'Start Date' },
  { key: 'endDate',     label: 'End Date' },
  { key: 'status',      label: 'Status' },
  { key: 'assigned',    label: 'Assigned' },
  { key: 'manager',     label: 'Client Manager' },
  { key: 'income',      label: 'Income' },
  { key: 'outcome',     label: 'Outcome' },
  { key: 'profit',      label: 'Profit' },
  { key: 'description', label: 'Description' },
];

const DEFAULT_VISIBLE = new Set([
  'name', 'company', 'type', 'location', 'startDate', 'endDate',
  'status', 'assigned', 'income', 'outcome', 'profit',
]);


const STATIC_PENDING = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}`,
      borderRadius: '4px', padding: '2px 10px',
      fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
}

// ─── Multi-select ─────────────────────────────────────────────────────────────

function MultiSelect({ options, selected = [], onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(p => !p)}
        style={{
          background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '4px',
          color: selected.length ? 'var(--text-body)' : 'var(--text-dim)',
          padding: '4px 8px', fontSize: '12px', cursor: 'pointer', userSelect: 'none',
        }}>
        {selected.length === 0 ? 'All' : selected.join(', ')}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '30px', left: 0,
          background: 'var(--bg-menu)', border: '1px solid var(--border-strong)',
          borderRadius: '8px', padding: '8px 12px',
          zIndex: 300, minWidth: '210px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {options.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-body)', fontSize: '13px', padding: '4px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => {
                  const next = selected.includes(opt)
                    ? selected.filter(x => x !== opt)
                    : [...selected, opt];
                  onChange(next);
                }}
              />
              {opt}
            </label>
          ))}
          {selected.length > 0 && (
            <div
              onClick={() => { onChange([]); setOpen(false); }}
              style={{ color: 'var(--accent)', fontSize: '11px', marginTop: '8px', cursor: 'pointer', borderTop: '1px solid var(--border)', paddingTop: '6px' }}>
              Clear
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── KPI Block ────────────────────────────────────────────────────────────────

function KPIBlock({ projects = [], freelancersCount = 0 }) {
  const live = projects.filter(p => !p.archived);
  const activeCount      = live.filter(p => p.status === 'Active').length;
  const totalFreelancers = freelancersCount;

  // "Monthly Income" і "Upcoming (30d)" картки прибрано за рішенням користувача
  // (29.07.2026) — прибиральна робота адмінського дашборду.
  const kpis = [
    { label: 'Active Projects',   value: activeCount,      icon: '📋', color: 'var(--success-text)' },
    { label: 'Total Freelancers', value: totalFreelancers, icon: '👥', color: 'var(--info-text)' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '40px' }}>
      {kpis.map(k => (
        <div key={k.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>{k.label}</span>
            <span style={{ fontSize: '20px' }}>{k.icon}</span>
          </div>
          <div style={{ color: k.color, fontSize: '28px', fontWeight: 'bold' }}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Pending Actions ──────────────────────────────────────────────────────────

const ACTION_TYPE_CONFIG = {
  registration:  { label: 'Registration', color: 'var(--info-text)', icon: '👤' },
  application:   { label: 'Application',  color: 'var(--warning-text)', icon: '📋' },
  document:      { label: 'Document',     color: 'var(--purple-text)', icon: '📁' },
  'task-overdue':{ label: 'Overdue',      color: 'var(--accent)', icon: '⚠️'  },
  'task-due':    { label: 'Due Soon',     color: 'var(--warning-text)', icon: '⏰'  },
};

function PendingActions({ pendingUsers = [], onApproveUser, onRejectUser, isSuperAdmin = false }) {
  const { getOverdueTasks, getDueSoonTasks, updateTask } = useTasks();
  const [dismissed, setDismissed] = useState([]);
  const [busyUserId, setBusyUserId] = useState(null);

  const overdue  = getOverdueTasks();
  const dueSoon  = getDueSoonTasks().filter(t => !overdue.find(o => o.id === t.id));

  const taskActions = [
    ...overdue.map(t => ({
      id:   `task-overdue-${t.id}`,
      type: 'task-overdue',
      text: t.title,
      sub:  `${t.projectName} · deadline: ${t.deadline}`,
      time: `Was due ${t.deadline}`,
      taskId: t.id,
    })),
    ...dueSoon.map(t => ({
      id:   `task-due-${t.id}`,
      type: 'task-due',
      text: t.title,
      sub:  `${t.projectName}`,
      time: `Due: ${t.deadline}`,
      taskId: t.id,
    })),
  ];

  // Реальні pending-реєстрації з БД
  const registrationActions = pendingUsers.map(u => ({
    id:   `reg-${u.id}`,
    type: 'registration',
    text: `New registration: ${u.name}`,
    sub:  [u.email, u.country, u.phone].filter(Boolean).join(' · '),
    time: u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '',
    userId: u.id,
  }));

  const allActions = [...registrationActions, ...taskActions, ...STATIC_PENDING];
  const visible    = allActions.filter(a => !dismissed.includes(a.id));

  const dismiss = id => setDismissed(prev => [...prev, id]);

  const markDone = (taskId, actionId) => {
    updateTask(taskId, { status: 'Done' });
    dismiss(actionId);
  };

  const handleUser = async (fn, userId, role) => {
    setBusyUserId(userId);
    try { await fn(userId, role); } finally { setBusyUserId(null); }
  };

  return (
    <div style={{ marginBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <p style={{ color: 'var(--text-dim)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
          Pending Actions
          {visible.length > 0 && (
            <span style={{ marginLeft: '8px', background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: '10px', padding: '1px 7px', fontSize: '10px' }}>
              {visible.length}
            </span>
          )}
        </p>
        {dismissed.length > 0 && (
          <span onClick={() => setDismissed([])} style={{ color: 'var(--text-dim)', fontSize: '12px', cursor: 'pointer' }}>
            Restore all
          </span>
        )}
      </div>
      {visible.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', textAlign: 'center', color: 'var(--text-disabled)', fontSize: '13px' }}>
          No pending actions
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {visible.map(action => {
            const cfg     = ACTION_TYPE_CONFIG[action.type];
            const isTask  = action.type === 'task-overdue' || action.type === 'task-due';
            return (
              <div key={action.id} style={{ background: 'var(--bg-card)', border: `1px solid ${isTask ? '#2a1a1a' : 'var(--border)'}`, borderLeft: isTask ? `3px solid ${cfg.color}` : '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '20px' }}>{cfg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{ color: cfg.color, fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '1px 6px' }}>
                      {cfg.label}
                    </span>
                    <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>{action.time}</span>
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold' }}>{action.text}</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{action.sub}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {isTask ? (
                    <>
                      <button
                        onClick={() => markDone(action.taskId, action.id)}
                        style={{ background: 'var(--success-text)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                        Mark Done
                      </button>
                      <button
                        onClick={() => dismiss(action.id)}
                        style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        Dismiss
                      </button>
                    </>
                  ) : action.userId ? (
                    <>
                      <button
                        disabled={busyUserId === action.userId}
                        onClick={() => handleUser(onApproveUser, action.userId, 'Freelancer')}
                        style={{ background: 'var(--success-text)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', opacity: busyUserId === action.userId ? 0.5 : 1 }}>
                        ✓ Freelancer
                      </button>
                      {isSuperAdmin && (
                        <button
                          disabled={busyUserId === action.userId}
                          onClick={() => window.confirm(`Надати ${action.text.replace('New registration: ', '')} права АДМІНІСТРАТОРА?`) && handleUser(onApproveUser, action.userId, 'Admin')}
                          style={{ background: 'var(--info-text)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', opacity: busyUserId === action.userId ? 0.5 : 1 }}>
                          ✓ Admin
                        </button>
                      )}
                      <button
                        disabled={busyUserId === action.userId}
                        onClick={() => window.confirm(`Відхилити реєстрацію ${action.text.replace('New registration: ', '')}? Акаунт буде видалено.`) && handleUser(onRejectUser, action.userId)}
                        style={{ background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', opacity: busyUserId === action.userId ? 0.5 : 1 }}>
                        ✕ Reject
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => dismiss(action.id)}
                      style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Activity Log (compact widget) ─────────────────────────────────────────────
// Останні кілька дій адмінів/фрілансерів прямо на dashboard, під Pending
// Actions (рішення 03.08.2026) — без деталей, лише "хто/що/коли", з
// посиланням на повний журнал (ActivityLogPage). Admin/SuperAdmin only —
// бекенд і так гейтить requireRole('Admin'), тут просто не рендеримо блок
// для HR/PM (див. виклик нижче, isSuperAdmin/isAdmin перевірка на рівні Dashboard).
function ActivityLogBlock({ onViewAll }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.activityLog.recent(8)
      .then(res => setEntries(res.entries || []))
      .catch(err => setError(err.message || 'Failed to load activity log'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ marginBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <p style={{ color: 'var(--text-dim)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
          Recent Activity
        </p>
        <span onClick={onViewAll} style={{ color: 'var(--text-dim)', fontSize: '12px', cursor: 'pointer' }}>
          View all →
        </span>
      </div>
      {loading ? (
        <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
      ) : error ? (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>
          ⚠️ {error}
        </div>
      ) : entries.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', textAlign: 'center', color: 'var(--text-disabled)', fontSize: '13px' }}>
          No activity recorded yet
        </div>
      ) : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {entries.map((e, i) => (
            <div key={e.id}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 18px', borderBottom: i < entries.length - 1 ? '1px solid var(--bg-subtle)' : 'none' }}>
              <span style={{ color: 'var(--text-disabled)', fontSize: '11px', width: '110px', flexShrink: 0 }}>{formatTime(e.createdAt)}</span>
              <span style={{ color: 'var(--text-body)', fontSize: '12px', width: '130px', flexShrink: 0 }}>{e.actorName || 'System'}</span>
              <span style={{ color: 'var(--text-primary)', fontSize: '12px' }}>{actionLabel(e.action)}</span>
              {e.targetLabel && <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>— {e.targetLabel}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Projects Table ───────────────────────────────────────────────────────────

function ProjectsTable({ currency, onNewProject, projects = [] }) {
  const [visibleCols, setVisibleCols]     = useState(new Set(DEFAULT_VISIBLE));
  const [showColPicker, setShowColPicker] = useState(false);
  const [showFilters, setShowFilters]     = useState(false);
  const [filters, setFilters]             = useState({});
  // Валюта для Grand Total (конвертований підсумок по всіх проєктах в одну
  // валюту) — стартує з валюти, обраної у верхньому меню, але керується
  // окремо, як і на сторінці Projects.
  const [grandCur, setGrandCur]           = useState(currency || 'EUR');

  const toggleCol = key => setVisibleCols(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });
  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const columns = ALL_COLUMNS.filter(c => visibleCols.has(c.key));

  const assignedNames = p => (p.assigned || []).map(a => typeof a === 'string' ? a : a.name);
  const managerStr = p => typeof p.manager === 'object' && p.manager
    ? `${p.manager.name || ''} ${p.manager.email || ''} ${p.manager.phone || ''}`
    : String(p.manager || '');

  const filtered = useMemo(() => projects.filter(p => !p.archived).filter(p =>
    columns.every(col => {
      const f = filters[col.key];
      if (!f || f.length === 0) return true;
      if (col.key === 'type')   return Array.isArray(f) ? f.includes(p.type)   : true;
      if (col.key === 'status') return Array.isArray(f) ? f.includes(p.status) : true;
      const fStr = String(f).toLowerCase().trim();
      if (!fStr) return true;
      const val = col.key === 'assigned' ? assignedNames(p).join(', ')
        : col.key === 'manager' ? managerStr(p)
        : String(p[col.key] ?? '');
      return val.toLowerCase().includes(fStr);
    })
  ), [projects, filters, columns]); // eslint-disable-line react-hooks/exhaustive-deps

  const thStyle = {
    padding: '10px 14px', textAlign: 'left', color: 'var(--text-dim)',
    fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
    letterSpacing: '1px', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap', background: 'var(--bg-card)',
  };
  const tdStyle = {
    padding: '11px 14px', color: 'var(--text-body)', fontSize: '13px',
    borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle', whiteSpace: 'nowrap',
  };
  const inputStyle = {
    width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-strong)',
    borderRadius: '4px', color: 'var(--text-body)', padding: '4px 8px',
    fontSize: '12px', boxSizing: 'border-box',
  };

  const renderFilterCell = col => {
    if (col.key === 'type')   return <MultiSelect options={PROJECT_TYPES} selected={filters[col.key] || []} onChange={v => setFilter(col.key, v)} />;
    if (col.key === 'status') return <MultiSelect options={STATUSES}      selected={filters[col.key] || []} onChange={v => setFilter(col.key, v)} />;
    return <input style={inputStyle} placeholder="Filter..." value={filters[col.key] || ''} onChange={e => setFilter(col.key, e.target.value)} />;
  };

  const renderCell = (p, key) => {
    switch (key) {
      case 'name':     return <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{p.name}</span>;
      case 'status':   return <>
        <StatusBadge status={p.status} />
        <PaymentStatusBadges statuses={p.paymentStatus} />
      </>;
      case 'assigned': {
        const names = assignedNames(p);
        return names.length ? names.join(', ') : <span style={{ color: 'var(--text-disabled)' }}>—</span>;
      }
      case 'manager': {
        if (!p.manager) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
        if (typeof p.manager === 'string') return p.manager;
        return (
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px' }}>{p.manager.name}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{p.manager.phone}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{p.manager.email}</div>
          </div>
        );
      }
      case 'income':  return <span style={{ color: 'var(--success-text)' }}>{fmtMoney(p.income,  curOf(p))}</span>;
      case 'outcome': return <span style={{ color: 'var(--accent)' }}>{fmtMoney(p.outcome, curOf(p))}</span>;
      case 'profit':  return <span style={{ color: p.profit >= 0 ? 'var(--success-text)' : 'var(--accent)' }}>{fmtMoney(p.profit, curOf(p))}</span>;
      default:        return p[key] || <span style={{ color: 'var(--text-disabled)' }}>—</span>;
    }
  };

  const activeFilterCount = Object.values(filters).filter(f => f && f.length > 0).length;

  // Підсумки — окремо по кожній валюті (проєкти можуть бути в різних
  // валютах, тож просте додавання сирих чисел раніше давало неправильні
  // цифри) + Grand Total, конвертований в одну обрану валюту.
  const totalsByCur = useMemo(() => {
    const m = {};
    filtered.forEach(p => {
      const c = curOf(p);
      if (!m[c]) m[c] = { income: 0, outcome: 0, profit: 0 };
      m[c].income  += p.income  || 0;
      m[c].outcome += p.outcome || 0;
      m[c].profit  += (p.profit ?? ((p.income || 0) - (p.outcome || 0)));
    });
    return m;
  }, [filtered]);

  const grandTotal = useMemo(() => {
    const target = CURRENCY_META[grandCur]?.rate || 1;
    return Object.entries(totalsByCur).reduce((acc, [c, t]) => {
      const rate = CURRENCY_META[c]?.rate || 1;
      const k = target / rate; // c → EUR → grandCur
      acc.income  += t.income  * k;
      acc.outcome += t.outcome * k;
      acc.profit  += t.profit  * k;
      return acc;
    }, { income: 0, outcome: 0, profit: 0 });
  }, [totalsByCur, grandCur]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <p style={{ color: 'var(--text-dim)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
          Project Status
          {activeFilterCount > 0 && (
            <span style={{ marginLeft: '8px', background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: '10px', padding: '1px 7px', fontSize: '10px' }}>
              {activeFilterCount}
            </span>
          )}
        </p>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setShowFilters(p => !p)}
            style={{ background: showFilters ? 'var(--border)' : 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            {showFilters ? 'Hide Filters' : 'Filter'}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={() => setFilters({})}
              style={{ background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              Clear All
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowColPicker(p => !p)}
              style={{ background: showColPicker ? 'var(--border)' : 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              Columns
            </button>
            {showColPicker && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px', padding: '12px 16px', zIndex: 200, minWidth: '200px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                <div style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>Show / Hide Columns</div>
                {ALL_COLUMNS.map(col => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-body)', fontSize: '13px', padding: '4px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onNewProject}
            style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
            + New Project
          </button>
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{columns.map(col => <th key={col.key} style={thStyle}>{col.label}</th>)}</tr>
            {showFilters && (
              <tr style={{ background: 'var(--bg-input)' }}>
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>
                    {renderFilterCell(col)}
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-disabled)', padding: '40px' }}>
                  No projects match the current filters
                </td>
              </tr>
            ) : filtered.map(p => (
              <tr key={p.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {columns.map(col => <td key={col.key} style={tdStyle}>{renderCell(p, col.key)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(visibleCols.has('income') || visibleCols.has('outcome') || visibleCols.has('profit')) && (
        <div style={{ marginTop: '12px', background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px' }}>
          {Object.entries(totalsByCur).map(([c, t]) => (
            <div key={c} style={{ display: 'flex', gap: '24px', alignItems: 'center', padding: '4px 0' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 'bold', width: '48px' }}>{c}</span>
              {visibleCols.has('income')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Income: <span style={{ color: 'var(--success-text)' }}>{fmtMoney(t.income, c)}</span></span>}
              {visibleCols.has('outcome') && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Outcome: <span style={{ color: 'var(--accent)' }}>{fmtMoney(t.outcome, c)}</span></span>}
              {visibleCols.has('profit')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Profit: <span style={{ color: t.profit >= 0 ? 'var(--success-text)' : 'var(--accent)' }}>{fmtMoney(t.profit, c)}</span></span>}
            </div>
          ))}

          {Object.keys(totalsByCur).length > 1 && (
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', padding: '8px 0 2px', borderTop: '1px solid var(--border)', marginTop: '6px' }}>
              <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                GRAND TOTAL
                <select
                  value={grandCur}
                  onChange={e => setGrandCur(e.target.value)}
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '4px', color: 'var(--text-body)', fontSize: '11px', padding: '2px 4px' }}>
                  {Object.keys(CURRENCY_META).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </span>
              {visibleCols.has('income')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Income: <span style={{ color: 'var(--success-text)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.income, grandCur)}</span></span>}
              {visibleCols.has('outcome') && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Outcome: <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.outcome, grandCur)}</span></span>}
              {visibleCols.has('profit')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Profit: <span style={{ color: grandTotal.profit >= 0 ? 'var(--success-text)' : 'var(--accent)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.profit, grandCur)}</span></span>}
              <span style={{ color: 'var(--text-disabled)', fontSize: '10px', marginLeft: 'auto' }}>за приблизним курсом</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard Inner ──────────────────────────────────────────────────────────

// ─── Reminders Block ─────────────────────────────────────────────────────────

function birthdayStatus(dob) {
  if (!dob) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const birth = new Date(dob);
  // This year's birthday
  let bday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  let days = Math.ceil((bday - today) / 86400000);
  // If already passed this year, check next year
  if (days < -7) {
    bday = new Date(today.getFullYear() + 1, birth.getMonth(), birth.getDate());
    days = Math.ceil((bday - today) / 86400000);
  }
  if (days < -7 || days > 30) return null; // only show ±7d past + 30d upcoming
  const age = today.getFullYear() - birth.getFullYear() + (days <= 0 ? 0 : -1);
  if (days < 0)  return { label: `${Math.abs(days)}d ago`,  color: 'var(--purple-text)', bg: 'var(--purple-bg)', days, age, bday };
  if (days === 0) return { label: 'Today! 🎂',              color: 'var(--purple-text)', bg: 'var(--purple-bg)', days, age, bday };
  return           { label: `in ${days}d`,                  color: 'var(--purple-text)', bg: 'var(--purple-bg)', days, age, bday };
}

function RemindersBlock({ freelancers }) {
  const docItems = [];
  const bdayItems = [];

  (freelancers || []).forEach(fr => {
    // Passport expiry (основний)
    if (fr.passportExpiry) {
      const st = docExpiryStatus(fr.passportExpiry);
      if (st && st.days <= 90) {
        docItems.push({ name: fr.name, docType: 'Passport', expiry: fr.passportExpiry, ...st });
      }
    }
    // Passport expiry (додаткові паспорти)
    (fr.additionalPassports || []).forEach(p => {
      if (p.expiry) {
        const st = docExpiryStatus(p.expiry);
        if (st && st.days <= 90) {
          docItems.push({ name: fr.name, docType: `Passport (${p.number || '?'})`, expiry: p.expiry, ...st });
        }
      }
    });
    // Documents array
    (fr.documents || []).forEach(doc => {
      if (doc.expiry) {
        const st = docExpiryStatus(doc.expiry);
        if (st && st.days <= 90) {
          docItems.push({ name: fr.name, docType: doc.type || 'Document', expiry: doc.expiry, ...st });
        }
      }
    });
    // Visas
    (fr.visas || []).forEach(visa => {
      if (visa.expiry) {
        const st = docExpiryStatus(visa.expiry);
        if (st && st.days <= 90) {
          docItems.push({ name: fr.name, docType: `Visa (${visa.country || '?'})`, expiry: visa.expiry, ...st });
        }
      }
    });
    // Birthdays
    if (fr.dob) {
      const bs = birthdayStatus(fr.dob);
      if (bs) {
        bdayItems.push({ name: fr.name, docType: `🎂 Birthday (turns ${bs.age + 1})`, expiry: bs.bday.toISOString().slice(0, 10), ...bs });
      }
    }
  });

  // Sort each group
  docItems.sort((a, b) => a.days - b.days);
  bdayItems.sort((a, b) => a.days - b.days);
  const items = [...docItems, ...bdayItems];

  return (
    <div style={{ marginBottom: '48px' }}>
      <p style={{ color: 'var(--text-dim)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '16px' }}>
        Reminders
      </p>

      {items.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 24px', color: 'var(--text-dim)', fontSize: '13px' }}>
          ✅ No documents expiring or birthdays within the next 30 days.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map((item, i) => (
            <div key={i} style={{
              background: 'var(--bg-card)',
              border: `1px solid ${item.days < 0 ? 'var(--accent)' : item.days <= 30 ? 'var(--accent)' : 'var(--warning-text)'}`,
              borderLeft: `4px solid ${item.color}`,
              borderRadius: '8px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}>
              <span style={{
                background: item.bg,
                color: item.color,
                fontWeight: 700,
                fontSize: '11px',
                padding: '3px 8px',
                borderRadius: '4px',
                minWidth: '70px',
                textAlign: 'center',
                flexShrink: 0,
              }}>
                {item.label}
              </span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px', minWidth: '150px' }}>
                {item.name}
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                {item.docType}
              </span>
              <span style={{ color: 'var(--text-dim)', fontSize: '12px', marginLeft: 'auto' }}>
                {formatDate(item.expiry)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const DASH_PAGES = ['dashboard', 'newProject', 'projects', 'tasks', 'freelancers', 'companies', 'equipment', 'calendar', 'profile', 'documents'];
const pageFromHash = () => {
  // .split('?')[0] — на мить після відкриття нової вкладки хеш ще може містити
  // "?t=<token>" (App.js прибирає його асинхронно), тому парсимо без query-частини.
  const h = window.location.hash.replace('#', '').split('?')[0];
  // Динамічний роут для картки фрілансера в окремій вкладці (#freelancer/123) —
  // не входить у фіксований список DASH_PAGES, тримаємо сирий hash як page.
  if (/^freelancer\/\d+$/.test(h)) return h;
  return DASH_PAGES.includes(h) ? h : 'dashboard';
};

// ─── Pending Tasks popup ──────────────────────────────────────────────────────
// Показується фрілансеру одразу після входу, якщо є задачі, не позначені Done.
// Закриття НЕ позначає задачі виконаними — тому попап зʼявиться знову при
// наступному вході (наступному монтуванні Dashboard), поки задачі не завершені.
const TASK_PRIORITY_COLOR = { Low: 'var(--text-dim)', Medium: 'var(--warning-text)', High: 'var(--accent)', Urgent: '#ff3860' };

function PendingTasksModal({ tasks, onClose, onStatusChange }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '520px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, sans-serif', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '16px' }}>✅ You have {tasks.length} open task{tasks.length > 1 ? 's' : ''}</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: '6px 0 0' }}>
              This will keep showing up when you sign in until every task below is marked Done.
            </p>
          </div>
          <span onClick={onClose} style={{ color: 'var(--text-dim)', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>✕</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tasks.map(t => {
            const overdue = t.deadline && t.deadline < today;
            return (
              <div key={t.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px' }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px' }}>{t.title}</div>
                {t.description && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>{t.description}</div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ color: TASK_PRIORITY_COLOR[t.priority] || 'var(--text-dim)', fontSize: '11px', fontWeight: 'bold' }}>{t.priority}</span>
                  {t.projectName && <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>· {t.projectName}</span>}
                  {t.deadline && (
                    <span style={{ color: overdue ? 'var(--accent)' : 'var(--text-dim)', fontSize: '11px' }}>
                      · {overdue ? '⚠️ ' : ''}Due {t.deadline}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  {t.status !== 'In Progress' && (
                    <button onClick={() => onStatusChange(t.id, 'In Progress')}
                      style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      Start
                    </button>
                  )}
                  <button onClick={() => onStatusChange(t.id, 'Done')}
                    style={{ background: 'var(--success-bg)', border: '1px solid var(--success-text)', color: 'var(--success-text)', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                    ✓ Mark as Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose}
            style={{ width: '100%', background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Close for now
          </button>
        </div>
      </div>
    </div>
  );
}

const DOC_CATEGORY_LABEL = { passport: 'Passport', photo: 'Photo', cv: 'CV', visa: 'Visa', other: 'Document', consent: 'Consent' };

// Спливне вікно "у вас є документи, що потребують виправлення" — показується
// при вході фрілансера в кабінет, поки лишається хоч один позначений 🚩
// документ (див. flaggedDocuments у FreelancersPage.js). За тим самим
// принципом, що й PendingTasksModal — закриття не знімає позначку, тож
// з'явиться знову при наступному вході, поки документ не виправлять.
function FlaggedDocsModal({ docs, onClose, onGoToDocuments }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--accent)', borderRadius: '12px', maxWidth: '480px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, sans-serif', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '16px' }}>🚩 {docs.length} document{docs.length > 1 ? 's' : ''} need{docs.length > 1 ? '' : 's'} correction</h3>
          <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: '6px 0 0' }}>
            You can't be assigned to projects until these are fixed. This will keep showing up when you sign in until every document below is corrected.
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {docs.map(d => (
            <div key={d.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px' }}>
                {DOC_CATEGORY_LABEL[d.category] || 'Document'}{d.name ? ` — ${d.name}` : ''}
              </div>
              <div style={{ color: 'var(--danger-text-soft)', fontSize: '12px', marginTop: '4px' }}>{d.flagReason}</div>
            </div>
          ))}
          <a href="/docs/Freelancer_Data_Entry_Guide.pdf" target="_blank" rel="noreferrer"
            style={{ color: 'var(--accent)', fontSize: '12px', marginTop: '2px', textDecoration: 'underline' }}>
            📄 See the Guide to Completing Your Personal Data for examples
          </a>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px' }}>
          <button onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Close for now
          </button>
          <button onClick={onGoToDocuments}
            style={{ flex: 1, background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            Go to My Documents
          </button>
        </div>
      </div>
    </div>
  );
}

// Спливне вікно "у вашій особистій папці на проєкті з'явився документ" —
// показується при вході фрілансера, поки лишаються непобачені сповіщення
// (freelancer_doc_notifications.seen_at IS NULL). На відміну від
// PendingTasksModal/FlaggedDocsModal (де умова показу переобчислюється щоразу
// з поточного стану), тут закриття явно позначає записи побаченими на
// бекенді — це одноразова черга подій, а не постійна умова.
function DocNotificationsModal({ notifications, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '480px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, sans-serif', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '16px' }}>📄 {notifications.length} update{notifications.length > 1 ? 's' : ''} in your folder</h3>
          <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: '6px 0 0' }}>
            Documents were added or changed in your personal folder on a project.
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {notifications.map(n => (
            <div key={n.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px' }}>{n.projectName || 'Project'}</div>
              {n.message && <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>{n.message}</div>}
            </div>
          ))}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose}
            style={{ width: '100%', background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            OK, got it
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardInner({ role, currentUserObj, onLogout, onUserUpdated }) {
  // Use currency from real user profile; fallback to EUR
  const [currency, setCurrency] = useState(currentUserObj?.currency || 'EUR');

  // Активна сторінка синхронізована з URL-хешем (#companies тощо):
  // переживає перезавантаження, працюють кнопки назад/вперед.
  const [page, setPageState] = useState(pageFromHash);
  const setPage = (p) => {
    window.location.hash = p === 'dashboard' ? '' : p;
    setPageState(p);
  };
  useEffect(() => {
    const onHash = () => setPageState(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // ── Data state ─────────────────────────────────────────────────────────────
  // Initialised from localStorage as a fast cache; API fetch overwrites on mount.
  const [projects,     setProjects]     = useState(() => {
    try { const s = localStorage.getItem('cp-projects');     return s ? JSON.parse(s) : mockProjects;   } catch { return mockProjects; }
  });
  const [companies,    setCompanies]    = useState(() => {
    try { const s = localStorage.getItem('cp-companies');    return s ? JSON.parse(s) : initCompanies;  } catch { return initCompanies; }
  });
  const [freelancers,  setFreelancers]  = useState(() => {
    try { const s = localStorage.getItem('cp-freelancers');  return s ? JSON.parse(s) : MOCK_FREELANCERS; } catch { return MOCK_FREELANCERS; }
  });
  const [equipment,    setEquipment]    = useState(() => {
    try { const s = localStorage.getItem('cp-equipment');    return s ? JSON.parse(s) : [];             } catch { return []; }
  });
  const [pendingUsers, setPendingUsers] = useState([]);
  const [adminUsers,   setAdminUsers]   = useState([]);
  const [allUsers,     setAllUsers]     = useState([]); // усі активні користувачі (для призначення задач)

  // ── Онбординг фрілансера ──────────────────────────────────────────────────
  // Власна картка з /api/freelancers/me. Поки onboardingComplete === false,
  // фрілансер бачить форму заповнення профілю замість дашборду.
  const [myFreelancer, setMyFreelancer]     = useState(null);
  const [onboardingReady, setOnboardingReady] = useState(role === 'admin'); // адмінів не чекаємо
  useEffect(() => {
    if (role === 'admin') return;
    let cancelled = false;
    api.freelancers.me()
      .then(res => { if (!cancelled) setMyFreelancer(res.freelancer); })
      .catch(() => { if (!cancelled) setMyFreelancer(null); })
      .finally(() => { if (!cancelled) setOnboardingReady(true); });
    return () => { cancelled = true; };
  }, [role]);

  // Непобачені сповіщення "документ додано у вашу папку" — окремо від
  // myFreelancer, бо це one-shot черга (seen_at), а не похідне поле профілю.
  const [docNotifications, setDocNotifications] = useState([]);
  useEffect(() => {
    if (role === 'admin') return;
    let cancelled = false;
    api.freelancers.myDocNotifications(true)
      .then(res => { if (!cancelled) setDocNotifications(res.notifications || []); })
      .catch(() => { if (!cancelled) setDocNotifications([]); });
    return () => { cancelled = true; };
  }, [role]);
  const closeDocNotifications = () => {
    const ids = docNotifications.map(n => n.id);
    setDocNotifications([]); // закриваємо модалку одразу, не чекаючи відповіді
    api.freelancers.markDocNotificationsSeen(ids).catch(() => {});
  };

  // Звичайний Admin бачить лише проєкти, де він у Assigned. SuperAdmin — усі.
  const visibleProjects = useMemo(() => {
    if (currentUserObj?.role !== 'Admin') return projects;
    return projects.filter(p => (p.assigned || []).some(a => a.isAdmin && a.userId === currentUserObj.id));
  }, [projects, currentUserObj]);

  // Затвердження / відхилення реєстрацій (Pending Actions)
  const approveUser = async (id, role = 'Freelancer') => {
    try {
      await api.users.approve(id, role);
      setPendingUsers(prev => prev.filter(u => u.id !== id));
    } catch (e) { alert('Не вдалося затвердити: ' + e.message); }
  };
  const rejectUser = async id => {
    try {
      await api.users.reject(id);
      setPendingUsers(prev => prev.filter(u => u.id !== id));
    } catch (e) { alert('Не вдалося відхилити: ' + e.message); }
  };
  const [dataLoading,  setDataLoading]  = useState(false);
  const [dataError,    setDataError]    = useState(null);

  // Persist to localStorage as offline cache
  useEffect(() => { try { localStorage.setItem('cp-freelancers', JSON.stringify(freelancers)); } catch {} }, [freelancers]);
  useEffect(() => { try { localStorage.setItem('cp-projects',    JSON.stringify(projects));    } catch {} }, [projects]);
  useEffect(() => { try { localStorage.setItem('cp-companies',   JSON.stringify(companies));   } catch {} }, [companies]);
  useEffect(() => { try { localStorage.setItem('cp-equipment',   JSON.stringify(equipment));   } catch {} }, [equipment]);

  // Load all data from API on mount
  useEffect(() => {
    setDataLoading(true);
    setDataError(null);
    Promise.all([
      api.freelancers.list(),
      api.projects.list(),
      api.companies.list(),
      api.equipment.list().catch(() => ({ equipment: null })), // не валити все, якщо ендпоінт недоступний
      api.users.list({ status: 'pending' }).catch(() => ({ users: null })),
      api.users.list({ status: 'active' }).catch(() => ({ users: null })),
    ])
      .then(([frRes, prRes, coRes, eqRes, puRes, auRes]) => {
        if (frRes.freelancers) setFreelancers(frRes.freelancers);
        if (prRes.projects)    setProjects(prRes.projects);
        if (coRes.companies)   setCompanies(coRes.companies);
        if (eqRes.equipment)   setEquipment(eqRes.equipment);
        if (puRes.users)       setPendingUsers(puRes.users);
        if (auRes.users)       setAdminUsers(auRes.users.filter(u => ['Admin', 'SuperAdmin'].includes(u.role)));
        if (auRes.users)       setAllUsers(auRes.users);
      })
      .catch(err => {
        console.warn('API unavailable, using cached data:', err.message);
        setDataError('Працюємо в офлайн-режимі. Дані з кешу.');
      })
      .finally(() => setDataLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { getPendingTasks, getOverdueTasks, updateTask } = useTasks();

  const pendingTasks = getPendingTasks();
  const overdueTasks = getOverdueTasks();
  const taskBadge    = pendingTasks.length;
  const hasOverdue   = overdueTasks.length > 0;

  // Спливне вікно з незавершеними задачами для фрілансера — закриття не
  // позначає задачі виконаними, тому воно з'явиться знову при наступному
  // вході (наступному монтуванні Dashboard), поки лишаються задачі не в Done.
  const [tasksModalDismissed, setTasksModalDismissed] = useState(false);
  // Так само, як tasksModalDismissed — закриття не знімає позначку з
  // документа, тож нагадування зʼявиться знову при наступному вході.
  const [flaggedDocsModalDismissed, setFlaggedDocsModalDismissed] = useState(false);

  const adminCards = [
    { title: 'Projects',    icon: '📋', desc: 'Manage & publish projects' },
    { title: 'Tasks',       icon: '✅', desc: 'Assign & track tasks' },
    { title: 'Freelancers', icon: '👥', desc: 'Manage your crew' },
    { title: 'Companies',   icon: '🏢', desc: 'Client companies' },
    { title: 'Equipment',   icon: '🔧', desc: 'Your equipment stock' },
    { title: 'Documents',   icon: '📁', desc: 'Send & receive files' },
    { title: 'Calendar',    icon: '📅', desc: 'Team schedule' },
    { title: 'Finance',     icon: '💰', desc: 'Contracts & invoices' },
    { title: 'Profile',     icon: '👤', desc: 'Your account settings' },
  ];

  const freelancerCards = [
    { title: 'Projects',     icon: '📋', desc: 'View & apply for projects' },
    { title: 'Tasks',        icon: '✅', desc: 'Your assigned tasks' },
    { title: 'My Documents', icon: '📁', desc: 'Your files & contracts' },
    { title: 'Calendar',     icon: '📅', desc: 'Your schedule' },
    { title: 'Profile',      icon: '👤', desc: 'Edit your profile' },
  ];

  const cards = role === 'admin' ? adminCards : freelancerCards;

  // Гейти для фрілансера, у порядку: обов'язкова інструкція ("Got it") →
  // електронний підпис згоди на обробку даних → онбординг-візард. Застосовується
  // і до вже активних фрілансерів (guide_ack_at/consent_signed_at були NULL для
  // всіх акаунтів до цього релізу) — без винятку, за рішенням від 29.07.2026.
  if (role !== 'admin' && onboardingReady && myFreelancer && !myFreelancer.guideAckAt) {
    return (
      <GuidePage onDone={guideAckAt => setMyFreelancer(f => ({ ...f, guideAckAt }))} />
    );
  }
  if (role !== 'admin' && onboardingReady && myFreelancer && !myFreelancer.consentSignedAt) {
    return (
      <ConsentSignPage
        freelancer={myFreelancer}
        onDone={consentSignedAt => setMyFreelancer(f => ({ ...f, consentSignedAt }))}
      />
    );
  }

  // Онбординг блокує решту порталу для фрілансера, поки профіль не заповнений.
  // Виняток: якщо профіль вже був заповнений, а onboardingComplete зараз
  // false ТІЛЬКИ через позначений 🚩 документ (computeOnboardingComplete на
  // бекенді враховує і це) — не женемо людину через весь візард з кроку 1,
  // а показуємо звичайний дашборд із нагадуванням нижче (FlaggedDocsModal).
  if (role !== 'admin' && onboardingReady && myFreelancer && !myFreelancer.onboardingComplete && flaggedDocuments(myFreelancer).length === 0) {
    return (
      <OnboardingPage
        freelancer={myFreelancer}
        onComplete={updated => setMyFreelancer(updated)}
      />
    );
  }

  // Картка фрілансера в окремій вкладці (#freelancer/123) — тільки для admin.
  const freelancerCardMatch = typeof page === 'string' && page.match(/^freelancer\/(\d+)$/);
  if (freelancerCardMatch) {
    if (role !== 'admin') {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', fontSize: '13px' }}>
          Insufficient permissions to view this page.
        </div>
      );
    }
    return <FreelancerCardPage freelancerId={Number(freelancerCardMatch[1])} currentUser={currentUserObj} allUsers={allUsers} projects={visibleProjects} />;
  }

  if (page === 'newProject')   return <ProjectForm onBack={() => setPage('dashboard')} />;
  if (page === 'projects' && role !== 'admin') return <MyProjectsPage onBack={() => setPage('dashboard')} />;
  if (page === 'projects')     return <ProjectsPage onBack={() => setPage('dashboard')} projects={visibleProjects} setProjects={setProjects} companies={companies} freelancers={freelancers} equipment={equipment} adminUsers={adminUsers} allUsers={allUsers} currentUser={currentUserObj} />;
  if (page === 'tasks')        return <TasksPage onBack={() => setPage('dashboard')} projects={visibleProjects} allUsers={allUsers} currentUser={currentUserObj} />;
  if (page === 'freelancers')  return <FreelancersPage currentUser={currentUserObj} onBack={() => setPage('dashboard')} freelancers={freelancers} setFreelancers={setFreelancers} allUsers={allUsers} projects={visibleProjects} />;
  if (page === 'companies')    return <CompaniesPage onBack={() => setPage('dashboard')} companies={companies} setCompanies={setCompanies} projects={projects} />;
  if (page === 'equipment')    return <EquipmentPage onBack={() => setPage('dashboard')} equipment={equipment} setEquipment={setEquipment} projects={projects} setProjects={setProjects} />;
  // Team Calendar (усі проєкти + зайнятість усіх фрілансерів + дні народження +
  // терміни документів) — Admin/SuperAdmin/HR/PM. Перевіряємо реальну роль
  // юзера (currentUserObj.role), а не спрощений admin/freelancer role-prop —
  // HR/PM зараз сидять у "freelancer"-оболонці дашборду (App.js), але мають
  // бачити командний календар, а не особистий.
  const canSeeTeamCalendar = ['Admin', 'SuperAdmin', 'HR', 'PM'].includes(currentUserObj?.role);
  if (page === 'calendar' && canSeeTeamCalendar) {
    return <TeamCalendarPage onBack={() => setPage('dashboard')} projects={projects} freelancers={freelancers} />;
  }
  if (page === 'calendar')     return <MyCalendarPage onBack={() => setPage('dashboard')} />;
  if (page === 'profile')      return <ProfilePage currentUser={currentUserObj} onBack={() => setPage('dashboard')} onUserUpdated={onUserUpdated} />;
  if (page === 'documents' && role !== 'admin') return <MyDocumentsPage onBack={() => setPage('dashboard')} />;
  if (page === 'activityLog') return <ActivityLogPage onBack={() => setPage('dashboard')} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {role !== 'admin' && !tasksModalDismissed && pendingTasks.length > 0 && (
        <PendingTasksModal
          tasks={pendingTasks}
          onClose={() => setTasksModalDismissed(true)}
          onStatusChange={(id, status) => updateTask(id, { status })}
        />
      )}

      {role !== 'admin' && !flaggedDocsModalDismissed && myFreelancer && flaggedDocuments(myFreelancer).length > 0 && (
        <FlaggedDocsModal
          docs={flaggedDocuments(myFreelancer)}
          onClose={() => setFlaggedDocsModalDismissed(true)}
          onGoToDocuments={() => { setFlaggedDocsModalDismissed(true); setPage('documents'); }}
        />
      )}

      {role !== 'admin' && docNotifications.length > 0 && (
        <DocNotificationsModal
          notifications={docNotifications}
          onClose={closeDocNotifications}
        />
      )}

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>CREW PORTAL</span>
          <span style={{ marginLeft: '12px', background: role === 'admin' ? 'var(--accent)' : 'var(--border-strong)', color: 'var(--on-accent)', fontSize: '11px', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
            {role}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {role === 'admin' && (
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--text-disabled)', color: 'var(--text-body)', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
              {Object.keys(CURRENCY_META).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {currentUserObj && (
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
              {currentUserObj.name}
              <span style={{ marginLeft: '6px', background: 'var(--border)', color: 'var(--text-muted)', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>
                {currentUserObj.role}
              </span>
            </span>
          )}
          <button onClick={onLogout} style={{ background: 'transparent', border: '1px solid var(--text-dim)', color: 'var(--text-secondary)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>
            Logout
          </button>
        </div>
      </div>

      <div style={{ padding: '40px 32px' }}>
        {dataError && (
          <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', color: 'var(--warning-text)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {dataError}
          </div>
        )}
        <p style={{ color: 'var(--text-dim)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '16px' }}>
          {role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '48px' }}>
          {cards.map(card => {
            const isTask = card.title === 'Tasks';
            const handleClick = () => {
              if (card.title === 'Projects')    setPage('projects');
              if (card.title === 'Tasks')       setPage('tasks');
              if (card.title === 'Freelancers') setPage('freelancers');
              if (card.title === 'Companies')   setPage('companies');
              if (card.title === 'Equipment')   setPage('equipment');
              if (card.title === 'Calendar')    setPage('calendar');
              if (card.title === 'Profile')     setPage('profile');
              if (card.title === 'My Documents') setPage('documents');
            };
            return (
              <div
                key={card.title}
                onClick={handleClick}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', cursor: 'pointer', position: 'relative' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>

                {/* Badge for Tasks card */}
                {isTask && taskBadge > 0 && (
                  <span style={{
                    position: 'absolute', top: '12px', right: '12px',
                    background: hasOverdue ? 'var(--accent)' : 'var(--warning-text)',
                    color: 'var(--text-primary)', borderRadius: '10px', padding: '1px 7px',
                    fontSize: '10px', fontWeight: 'bold', lineHeight: '16px',
                  }}>
                    {taskBadge}
                  </span>
                )}

                <div style={{ fontSize: '26px', marginBottom: '10px' }}>{card.icon}</div>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 4px 0', fontSize: '14px' }}>{card.title}</h3>
                <p style={{ color: 'var(--text-dim)', margin: 0, fontSize: '11px' }}>{card.desc}</p>
                {isTask && taskBadge > 0 && (
                  <p style={{ color: hasOverdue ? 'var(--accent)' : 'var(--warning-text)', margin: '6px 0 0 0', fontSize: '11px', fontWeight: 'bold' }}>
                    {hasOverdue ? `${overdueTasks.length} overdue` : `${taskBadge} pending`}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {role === 'admin' && (
          <>
            <KPIBlock projects={visibleProjects} freelancersCount={freelancers.length} />
            <PendingActions
              pendingUsers={pendingUsers}
              onApproveUser={approveUser}
              onRejectUser={rejectUser}
              isSuperAdmin={currentUserObj?.role === 'SuperAdmin'}
            />
            <ActivityLogBlock onViewAll={() => setPage('activityLog')} />
            <RemindersBlock freelancers={freelancers} />
            <ProjectsTable currency={currency} onNewProject={() => setPage('newProject')} projects={visibleProjects} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard (root — provides context) ─────────────────────────────────────

function Dashboard({ role, currentUser: currentUserObj, onLogout, onUserUpdated }) {
  // currentUserObj = { id, name, email, role, currency } from App.js (real auth)
  return (
    <TasksProvider currentUser={currentUserObj}>
      <DashboardInner role={role} currentUserObj={currentUserObj} onLogout={onLogout} onUserUpdated={onUserUpdated} />
    </TasksProvider>
  );
}

export default Dashboard;
